import { Fragment } from 'react';
import { Box, Divider, Typography, useTheme } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
// Components
import ItemList, { Item } from './ItemList';
import { PointsLogSkeleton } from './SkeletonPoints';

// Constants
import { TYPOGRAPHY } from '../../ListContentPage/constants';
import { apiUrls } from '../../../constants/apiUrls';
import { QueryKeys } from '../../../constants/queryKeys';
import { getCardSx } from '@/theme/utils';

// Services
import { get } from '../../../services/api';

export default function List() {
  const { t } = useTranslation();
  const theme = useTheme();

  const getPointsLogs = async (page: number) => {
    const offset = page * 50;
    return get(apiUrls.users.points_logger + `?limit=50&offset=${offset}`);
  };

  const { data, fetchNextPage, isLoading, isError, isSuccess } =
    useInfiniteQuery({
      queryKey: [QueryKeys.POINTS_LOGS],
      queryFn: ({ pageParam }: { pageParam: number }) =>
        getPointsLogs(pageParam),
      initialPageParam: 0,
      getNextPageParam: (_lastpage, _allPages, lastPageParam) => {
        return lastPageParam + 1;
      },
    });

  const dataParser = (allPages: any) => {
    if (!allPages) {
      return [];
    }
    return allPages?.map((page: any) => page?.data?.results).flat();
  };

  const hasMore = (allPages: any) => {
    if (!allPages.length) return false;
    return allPages[allPages.length - 1].data?.next !== null;
  };

  return (
    <>
      <Box sx={{ ...getCardSx(theme), cursor: 'default', mt: 2 }}>
        <Typography
          sx={{
            fontFamily: TYPOGRAPHY.bold,
            fontSize: theme.fontSizes.subtitle,
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 2,
          }}
        >
          {t('pointsScreen.historicalPoints')}
        </Typography>

        {isLoading && <PointsLogSkeleton />}
        {isError && (
          <Typography
            sx={{
              fontSize: theme.fontSizes.body,
              color: theme.palette.text.secondary,
              textAlign: 'center',
              py: 2,
            }}
          >
            {t('pointsScreen.error')}
          </Typography>
        )}
        {!isLoading && (
          <InfiniteScroll
            dataLength={dataParser(data?.pages).length}
            next={fetchNextPage}
            hasMore={hasMore(data?.pages ?? [])}
            loader={undefined}
          >
            {isSuccess &&
              dataParser(data.pages)?.map((item: Item, index: number) => (
                <Fragment key={item?.created}>
                  {index % 5 === 0 && (
                    <Divider sx={{ borderColor: theme.palette.divider }} />
                  )}
                  <ItemList {...item} />
                </Fragment>
              ))}
          </InfiniteScroll>
        )}
      </Box>
    </>
  );
}
